import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, Users, AlertTriangle, HelpCircle,
  Globe, Settings, MessageCircle, ChevronRight,
  DoorOpen, Eye, Plus, Search
} from 'lucide-react';
import api from '../utils/api';
import { useI18n } from '../contexts/I18nContext';
import { toast } from 'sonner';

const Community = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('forum'); // 'forum' or 'chat'
  const [forumCategories, setForumCategories] = useState([]);
  const [chatRooms, setChatRooms] = useState([]);
  const [roomRequests, setRoomRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disclaimer, setDisclaimer] = useState('');

  useEffect(() => {
    if (activeTab === 'forum') {
      fetchForumCategories();
    } else {
      fetchChatRooms();
    }
  }, [activeTab]);

  const fetchForumCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/forum/categories');
      setForumCategories(response.data.categories);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      toast.error(t('community.loadCategoriesFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchChatRooms = async () => {
    try {
      setLoading(true);
      const [roomsRes, requestsRes] = await Promise.all([
        api.get('/chat/rooms'),
        api.get('/chat/requests')
      ]);
      setChatRooms(roomsRes.data.rooms);
      setDisclaimer(roomsRes.data.disclaimer);
      setRoomRequests(requestsRes.data.requests);
    } catch (err) {
      console.error('Failed to fetch chat rooms:', err);
      toast.error(t('community.loadRoomsFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (iconName) => {
    const icons = {
      'languages': <Globe className="w-6 h-6" />,
      'settings': <Settings className="w-6 h-6" />,
      'alert-triangle': <AlertTriangle className="w-6 h-6" />,
      'message-circle': <MessageCircle className="w-6 h-6" />
    };
    return icons[iconName] || <MessageSquare className="w-6 h-6" />;
  };

  const getTotalParticipants = (rooms) => {
    return rooms.reduce((sum, r) => sum + r.participants + r.watchers, 0);
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">{t('community.title')}</h1>
          <p className="text-slate-400">{t('community.subtitle')}</p>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('forum')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'forum'
                ? 'bg-amber-500 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
            data-testid="tab-forum"
          >
            <MessageSquare className="w-5 h-5" />
            {t('community.forum')}
          </button>
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'chat'
                ? 'bg-amber-500 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
            data-testid="tab-chat"
          >
            <Users className="w-5 h-5" />
            {t('community.chatRooms')}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
          </div>
        ) : activeTab === 'forum' ? (
          /* Forum View */
          <div className="space-y-4">
            {forumCategories.map((category) => (
              <div
                key={category.id}
                onClick={() => navigate(`/community/forum/${category.id}`)}
                className="p-6 rounded-xl bg-slate-800 border border-slate-700 hover:border-amber-500/50 cursor-pointer transition-all group"
                data-testid={`forum-category-${category.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-amber-500/20 text-amber-400">
                    {getCategoryIcon(category.icon)}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white group-hover:text-amber-400 transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-slate-400 text-sm">{category.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-amber-400">{category.post_count}</p>
                    <p className="text-xs text-slate-500">{t('community.posts')}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-amber-400 transition-colors" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Chat Rooms View */
          <div className="space-y-6">
            {/* Disclaimer */}
            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 text-sm">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p>{disclaimer}</p>
              </div>
            </div>

            {/* Chat Rooms */}
            <div className="space-y-4">
              {chatRooms.map((langGroup) => (
                <div key={langGroup.language_code} className="rounded-xl bg-slate-800 border border-slate-700 overflow-hidden">
                  <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-amber-400" />
                      <h3 className="text-lg font-semibold text-white">{langGroup.language_name}</h3>
                    </div>
                    <span className="text-sm text-slate-400">
                      {t('community.online', { count: getTotalParticipants(langGroup.rooms) })}
                    </span>
                  </div>
                  
                  <div className="divide-y divide-slate-700">
                    {langGroup.rooms.map((room) => (
                      <div key={room.room_id} className="p-4 flex items-center justify-between hover:bg-slate-700/50 transition-colors">
                        <div>
                          <p className="text-white font-medium">
                            {t('community.roomName', { number: room.room_number })}
                            {room.participants >= 10 && (
                              <span className="ml-2 text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded">{t('community.full')}</span>
                            )}
                          </p>
                          <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                            <span className="flex items-center gap-1">
                              <DoorOpen className="w-4 h-4" />
                              {t('community.participantsCount', { current: room.participants })}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="w-4 h-4" />
                              {t('community.watchingCount', { count: room.watchers })}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/community/chat/${room.room_id}?mode=watch`)}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
                            data-testid={`watch-${room.room_id}`}
                          >
                            <Eye className="w-4 h-4" />
                            {t('chat.watch')}
                          </button>
                          <button
                            onClick={() => navigate(`/community/chat/${room.room_id}?mode=participate`)}
                            disabled={room.participants >= 10}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                              room.participants >= 10
                                ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                : 'bg-amber-500 hover:bg-amber-600 text-white'
                            }`}
                            data-testid={`enter-${room.room_id}`}
                          >
                            <DoorOpen className="w-4 h-4" />
                            {t('community.enter')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Room Requests */}
            {roomRequests.length > 0 && (
              <div className="rounded-xl bg-slate-800 border border-slate-700 overflow-hidden">
                <div className="p-4 border-b border-slate-700">
                  <h3 className="text-lg font-semibold text-white">{t('community.pendingRequests')}</h3>
                  <p className="text-sm text-slate-400">{t('community.voteForLanguages')}</p>
                </div>
                <div className="divide-y divide-slate-700">
                  {roomRequests.map((req) => (
                    <div key={req.language_code} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{req.language_name}</p>
                        <p className="text-sm text-slate-400">{t('community.votes', { current: req.vote_count })}</p>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await api.post('/chat/room/request', {
                              language_code: req.language_code,
                              language_name: req.language_name
                            });
                            toast.success(t('community.voteRecorded'));
                            fetchChatRooms();
                          } catch (err) {
                            toast.error(t('community.voteFailed'));
                          }
                        }}
                        className="px-4 py-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 transition-colors"
                      >
                        {t('community.vote')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Request New Room Button */}
            <button
              onClick={() => navigate('/community/chat/request')}
              className="w-full p-4 rounded-xl bg-slate-800 border border-dashed border-slate-600 hover:border-amber-500/50 text-slate-400 hover:text-amber-400 transition-colors flex items-center justify-center gap-2"
              data-testid="request-room-btn"
            >
              <Plus className="w-5 h-5" />
              {t('community.createRoom')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Community;
